import React, { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Shield,
  Lock,
  KeyRound,
  Eye,
  EyeOff,
  Laptop,
  Smartphone,
  Tablet,
  Monitor,
  LogOut,
  RefreshCw,
  MapPin,
  Globe,
  Fingerprint,
  Clock,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";

const SecuritySettings = () => {
  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    logoutOtherDevices: true,
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isRevokingOther, setIsRevokingOther] = useState(false);
  const [selectedFingerprint, setSelectedFingerprint] = useState(null);

  // Fetch Active Sessions
  const fetchSessions = useCallback(async () => {
    try {
      setIsLoadingSessions(true);
      const res = await api.get("/user/sessions");
      if (res.data.success) {
        setSessions(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch active sessions:", err);
      const errMsg = err.response?.data?.message || "Failed to load active login devices.";
      toast.error(errMsg);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Password validation checks
  const newPass = passwordData.newPassword;
  const hasMinLength = newPass.length >= 8;
  const hasUpper = /[A-Z]/.test(newPass);
  const hasLower = /[a-z]/.test(newPass);
  const hasNumber = /\d/.test(newPass);
  const hasSpecial = /[@$!%*?&#^()_+={}[\]:;<>,.?~\\/-]/.test(newPass);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasNumber && hasSpecial;
  const isMatch = newPass && passwordData.confirmPassword && newPass === passwordData.confirmPassword;

  // Handle password form submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword) {
      toast.error("Please enter your current password.");
      return;
    }

    if (!isPasswordValid) {
      toast.error("New password must fulfill all security criteria.");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }

    try {
      setIsChangingPassword(true);
      const res = await api.post("/user/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        logoutOtherDevices: passwordData.logoutOtherDevices,
      });

      if (res.data.success) {
        toast.success(res.data.message || "Password updated successfully!");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
          logoutOtherDevices: true,
        });
        // Refresh sessions list in case other sessions were revoked
        fetchSessions();
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update password.";
      toast.error(msg);
      console.error("Change password error:", err);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Revoke a specific session
  const handleRevokeSession = async (session) => {
    const confirm = await Swal.fire({
      title: "Log out this device?",
      text: `Are you sure you want to log out "${session.browser} on ${session.os}"? This device will lose access immediately.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Log Out",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await api.post(`/user/sessions/${session._id}/revoke`);
      if (res.data.success) {
        toast.success(res.data.message || "Device logged out successfully.");
        setSessions((prev) => prev.filter((s) => s._id !== session._id));
      }
    } catch (err) {
      console.error("Error revoking session:", err);
      toast.error(err.response?.data?.message || "Failed to log out device.");
    }
  };

  // Revoke all other sessions
  const handleRevokeAllOthers = async () => {
    const otherCount = sessions.filter((s) => !s.isCurrent).length;
    if (otherCount === 0) {
      toast.info("No other active devices found.");
      return;
    }

    const confirm = await Swal.fire({
      title: "Log out all other devices?",
      text: `This will immediately terminate ${otherCount} other active login session(s). Only this device will stay logged in.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748b",
      confirmButtonText: `Yes, Log Out ${otherCount} Device(s)`,
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsRevokingOther(true);
      const res = await api.post("/user/sessions/revoke-others");
      if (res.data.success) {
        toast.success(res.data.message || "All other devices logged out.");
        fetchSessions();
      }
    } catch (err) {
      console.error("Error revoking other sessions:", err);
      toast.error(err.response?.data?.message || "Failed to log out other devices.");
    } finally {
      setIsRevokingOther(false);
    }
  };

  // Device Icon Helper
  const renderDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case "mobile":
        return <Smartphone className="w-5 h-5 text-indigo-500" />;
      case "tablet":
        return <Tablet className="w-5 h-5 text-amber-500" />;
      case "desktop":
      default:
        return <Laptop className="w-5 h-5 text-teal-600" />;
    }
  };

  // Time format helper
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Recently";
    const date = new Date(dateString);
    const now = new Date();
    const diffSec = Math.floor((now - date) / 1000);

    if (diffSec < 60) return "Active just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div id="security" className="space-y-6">
      {/* ── Section Title Banner ── */}
      <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/60 shadow-sm flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
          <Shield className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-800 tracking-tight">
            Security & Login Devices
          </h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">
            Manage your restaurant password, monitor active login devices, and remotely log out unrecognized sessions.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Left Column: Reset / Change Password (5 cols) ── */}
        <div className="lg:col-span-5 bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm space-y-5">
          <div className="border-b border-slate-100 pb-3 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-teal-600" />
            <div>
              <h3 className="text-base font-bold text-slate-800">Change Password</h3>
              <p className="text-[11px] text-slate-400 font-medium">
                Update your restaurant login password securely
              </p>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  placeholder="Enter current password"
                  required
                  className="w-full text-xs font-semibold px-3 py-2.5 pr-10 rounded-xl border border-slate-200 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-slate-800 placeholder-slate-400 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  placeholder="Enter new password"
                  required
                  className="w-full text-xs font-semibold px-3 py-2.5 pr-10 rounded-xl border border-slate-200 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-slate-800 placeholder-slate-400 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password strength checklist */}
              {newPass && (
                <div className="mt-2.5 p-2.5 bg-slate-50 border border-slate-100 rounded-xl space-y-1 text-[11px]">
                  <p className="font-bold text-slate-600 mb-1">Password Requirements:</p>
                  <div className="grid grid-cols-2 gap-1 font-medium">
                    <span className={`flex items-center gap-1 ${hasMinLength ? "text-emerald-600 font-bold" : "text-slate-400"}`}>
                      {hasMinLength ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : "•"} 8+ Characters
                    </span>
                    <span className={`flex items-center gap-1 ${hasUpper ? "text-emerald-600 font-bold" : "text-slate-400"}`}>
                      {hasUpper ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : "•"} Uppercase (A-Z)
                    </span>
                    <span className={`flex items-center gap-1 ${hasLower ? "text-emerald-600 font-bold" : "text-slate-400"}`}>
                      {hasLower ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : "•"} Lowercase (a-z)
                    </span>
                    <span className={`flex items-center gap-1 ${hasNumber ? "text-emerald-600 font-bold" : "text-slate-400"}`}>
                      {hasNumber ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : "•"} Number (0-9)
                    </span>
                    <span className={`flex items-center gap-1 col-span-2 ${hasSpecial ? "text-emerald-600 font-bold" : "text-slate-400"}`}>
                      {hasSpecial ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : "•"} Special Symbol (@$!%*?&)
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  placeholder="Re-type new password"
                  required
                  className={`w-full text-xs font-semibold px-3 py-2.5 pr-10 rounded-xl border ${
                    passwordData.confirmPassword && !isMatch
                      ? "border-red-400 focus:ring-red-200"
                      : "border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                  } focus:outline-none focus:ring-2 text-slate-800 placeholder-slate-400 transition`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordData.confirmPassword && (
                <p className={`text-[11px] font-bold mt-1 ${isMatch ? "text-emerald-600" : "text-red-500"}`}>
                  {isMatch ? "✓ Passwords match" : "✗ Passwords do not match"}
                </p>
              )}
            </div>

            {/* Logout other devices checkbox */}
            <div className="pt-1">
              <label className="flex items-start gap-2 text-xs font-medium text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={passwordData.logoutOtherDevices}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, logoutOtherDevices: e.target.checked })
                  }
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <span>Log out all other devices after changing password (Recommended)</span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isChangingPassword || !isPasswordValid || !isMatch}
              className="w-full bg-slate-900 hover:bg-black text-white font-bold py-2.5 px-4 rounded-xl text-xs shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isChangingPassword ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock className="w-3.5 h-3.5" />
                  Update Password
                </>
              )}
            </button>
          </form>
        </div>

        {/* ── Right Column: Active Devices & Sessions (7 cols) ── */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm space-y-4">
          <div className="border-b border-slate-100 pb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-teal-600" />
                <h3 className="text-base font-bold text-slate-800">
                  Logged In Devices
                </h3>
                <span className="text-[10px] font-black uppercase tracking-wider bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full">
                  {sessions.length} Active
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                Devices currently authenticated to your restaurant account
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchSessions}
                disabled={isLoadingSessions}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition"
                title="Refresh sessions"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingSessions ? "animate-spin" : ""}`} />
              </button>

              {sessions.filter((s) => !s.isCurrent).length > 0 && (
                <button
                  onClick={handleRevokeAllOthers}
                  disabled={isRevokingOther}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {isRevokingOther ? "Logging out..." : "Log Out All Other Devices"}
                </button>
              )}
            </div>
          </div>

          {/* Sessions List */}
          {isLoadingSessions ? (
            <div className="py-12 text-center text-slate-400 space-y-2">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-teal-500" />
              <p className="text-xs font-semibold">Loading logged-in devices...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-10 text-center text-slate-400">
              <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-xs font-semibold">No active sessions found.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
              {sessions.map((session) => (
                <div
                  key={session._id}
                  className={`p-4 rounded-2xl border transition ${
                    session.isCurrent
                      ? "border-teal-200 bg-teal-50/30"
                      : "border-slate-100 hover:border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    {/* Device Icon & Info */}
                    <div className="flex items-start gap-3">
                      <div className="p-2.5 rounded-xl bg-white border border-slate-200 shadow-sm shrink-0">
                        {renderDeviceIcon(session.deviceType)}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-xs font-bold text-slate-800">
                            {session.browser} {session.browserVersion && `(${session.browserVersion.split(".")[0]})`} on {session.os}
                          </h4>
                          {session.isCurrent ? (
                            <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              This Device
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                              Remote Device
                            </span>
                          )}
                        </div>

                        {/* Location and IP */}
                        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {session.location?.city
                              ? `${session.location.city}${session.location.region ? `, ${session.location.region}` : ""}${session.location.country ? ` (${session.location.country})` : ""}`
                              : "Localhost / Local Network"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3 text-slate-400" />
                            {session.ip || "127.0.0.1"}
                          </span>
                        </div>

                        {/* Timing and Fingerprint Info */}
                        <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-400 mt-1.5 font-medium">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-slate-400" />
                            {formatTimeAgo(session.lastActiveAt)}
                          </span>
                          <span className="text-slate-300">•</span>
                          <span>
                            Logged in: {new Date(session.loginAt).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {session.fingerprint && (
                            <>
                              <span className="text-slate-300">•</span>
                              <button
                                type="button"
                                onClick={() => setSelectedFingerprint(session)}
                                className="flex items-center gap-1 text-teal-600 hover:text-teal-700 font-bold underline underline-offset-2"
                              >
                                <Fingerprint className="w-3 h-3" />
                                {session.fingerprint.substring(0, 10)}...
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 self-end sm:self-center">
                      {!session.isCurrent && (
                        <button
                          onClick={() => handleRevokeSession(session)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50 text-xs font-bold transition shadow-2xs"
                        >
                          <LogOut className="w-3 h-3" />
                          Log Out
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Fingerprint Details Modal ── */}
      {selectedFingerprint && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Fingerprint className="w-5 h-5 text-teal-600" />
                <h3 className="text-base font-bold text-slate-800">Device Fingerprint Info</h3>
              </div>
              <button
                onClick={() => setSelectedFingerprint(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Fingerprint Hash</span>
                <p className="font-mono font-bold text-slate-800 bg-slate-50 border border-slate-200/60 p-2 rounded-xl mt-1 break-all">
                  {selectedFingerprint.fingerprint}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Platform</span>
                  <p className="font-bold text-slate-700 mt-0.5">
                    {selectedFingerprint.clientInfo?.platform || selectedFingerprint.os || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Screen Resolution</span>
                  <p className="font-bold text-slate-700 mt-0.5">
                    {selectedFingerprint.clientInfo?.screenWidth && selectedFingerprint.clientInfo?.screenHeight
                      ? `${selectedFingerprint.clientInfo.screenWidth} x ${selectedFingerprint.clientInfo.screenHeight} (${selectedFingerprint.clientInfo.pixelRatio || 1}x)`
                      : "Standard"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Language</span>
                  <p className="font-bold text-slate-700 mt-0.5">
                    {selectedFingerprint.clientInfo?.language || "en"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Timezone</span>
                  <p className="font-bold text-slate-700 mt-0.5">
                    {selectedFingerprint.clientInfo?.timezone || selectedFingerprint.location?.timezone || "UTC"}
                  </p>
                </div>
              </div>

              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">User Agent</span>
                <p className="font-mono text-[10px] text-slate-600 bg-slate-50 border border-slate-200/60 p-2 rounded-xl mt-1 break-all max-h-24 overflow-y-auto">
                  {selectedFingerprint.userAgent || "N/A"}
                </p>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedFingerprint(null)}
                className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-black transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
